import type {DiffUser} from '@github-ui/diff-lines/types'
import type {Repository} from '@github-ui/current-repository'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {memo, type CSSProperties} from 'react'
import type {VirtualItem} from '@tanstack/react-virtual'
import type {ProgressiveDiffEntry} from '../types/progressive-diff-types'
import type {DiffEntry} from '@github-ui/diff-lines'
import {DiffEntryLoadingSkeleton} from './progressive-diff/DiffEntryLoadingSkeleton'
import {PullRequestDiff} from './PullRequestDiff'
import styles from './PullRequestVirtualizedDiffRow.module.css'
interface VirtualizedDiffRowProps {
  virtualRow: VirtualItem
  virtualizedDiffEntry: ProgressiveDiffEntry | undefined
  diffEntry: DiffEntry | undefined
  diffSummary: PullRequestFileTreeDiff | undefined
  scrollMargin: number
  measureElement: (node: Element | null) => void
  basePath: string
  currentUser: DiffUser
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  selectedPathDigest: string | undefined
}

const MemoizedVirtualizedDiffRow = memo(function VirtualizedDiffRow({
  virtualRow,
  virtualizedDiffEntry,
  diffEntry,
  diffSummary,
  scrollMargin,
  measureElement,
  basePath,
  currentUser,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  repository,
  selectedPathDigest,
}: VirtualizedDiffRowProps) {
  if (!virtualizedDiffEntry) return null

  const virtualRowStyle: CSSProperties = {
    transform: `translateY(${virtualRow.start - scrollMargin}px)`,
  }

  if (virtualizedDiffEntry.loadingStatus === 'NotLoaded') {
    let approximateLineCount = diffSummary?.linesChanged ?? 5
    if (diffSummary?.changeType === 'REMOVED' || diffSummary?.changeType === 'DELETED') {
      approximateLineCount = 5
    }

    return (
      <div
        style={virtualRowStyle}
        className={styles.virtualizedDiffRow}
        ref={measureElement}
        data-index={virtualRow.index}
      >
        <DiffEntryLoadingSkeleton
          ariaLabel={`Loading ${virtualizedDiffEntry.path}`}
          testId={`lazy-load-${virtualizedDiffEntry.path}`}
          id={`diff-${virtualizedDiffEntry.pathDigest}`}
          approximateLineCount={approximateLineCount}
        />
      </div>
    )
  }

  if (!diffEntry) return null

  return (
    <div
      style={virtualRowStyle}
      className={styles.virtualizedDiffRow}
      ref={measureElement}
      data-index={virtualRow.index}
    >
      <PullRequestDiff
        basePath={basePath}
        changeType={diffEntry.status}
        currentUser={currentUser}
        diffLines={diffEntry.diffLines}
        diffManuallyExpanded={false}
        diffSize={diffEntry.diffSize}
        headerStickyOffset={-(virtualRow.start - scrollMargin)}
        headBranchName={headBranchName}
        headRepoName={headRepoName}
        headRepoOwner={headRepoOwner}
        helpUrl={diffEntry.helpUrl}
        isBinary={diffEntry.isBinary}
        isSelected={selectedPathDigest === undefined ? undefined : diffEntry.pathDigest === selectedPathDigest}
        isSubmodule={diffEntry.isSubmodule}
        isTooBig={diffEntry.isTooBig}
        linesAdded={diffEntry.linesAdded}
        linesChanged={diffEntry.linesChanged}
        linesDeleted={diffEntry.linesDeleted}
        newCommitOid={diffEntry.newCommitOid}
        newTreeEntry={diffEntry.newTreeEntry}
        objectId={diffEntry.objectId}
        oldCommitOid={diffEntry.oldCommitOid}
        oldTreeEntry={diffEntry.oldTreeEntry}
        path={diffEntry.path}
        pathDigest={diffEntry.pathDigest}
        pullRequestGlobalRelayId={pullRequestGlobalRelayId}
        repository={repository}
        reviewed={diffEntry.reviewed ?? false}
        richDiff={diffEntry.richDiff}
        status={diffEntry.status}
        submodule={diffEntry.submodule}
        truncatedReason={diffEntry.truncatedReason}
      />
    </div>
  )
})

export {MemoizedVirtualizedDiffRow as VirtualizedDiffRow}

try{ MemoizedVirtualizedDiffRow.displayName ||= 'MemoizedVirtualizedDiffRow' } catch {}